import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner, Alert } from "react-bootstrap";
import { CheckCircle, XCircle, AlertCircle } from "react-feather";
import "./PaymentCallback.scss";

/**
 * Generic Payment Callback Page
 * Handles payment responses from both HBL and Bank Alfalah
 * Works with static URL (no classroomId in route)
 */
const PaymentCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState("processing"); // processing, success, failure, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    processPaymentResponse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processPaymentResponse = () => {
    try {
      // Extract all query parameters
      const responseCode = searchParams.get("responseCode");
      const responseMessage = searchParams.get("responseMessage");
      const transactionId =
        searchParams.get("transactionId") || searchParams.get("TransactionId");
      const amount = searchParams.get("amount") || searchParams.get("Amount");
      const classroomId = searchParams.get("classroomId"); // May or may not be present
      const orderId =
        searchParams.get("orderId") || searchParams.get("OrderId");

      // Bank Alfalah specific parameter - RC field
      const rc = searchParams.get("RC");

      // console.log("Payment callback parameters:", {
      //   responseCode,
      //   rc,
      //   transactionId,
      //   amount,
      //   classroomId,
      //   orderId,
      // });

      // Store payment details
      const details = {
        responseCode: responseCode || rc,
        responseMessage: responseMessage
          ? decodeURIComponent(responseMessage)
          : null,
        transactionId: transactionId || null,
        amount: amount || null,
        classroomId,
        orderId,
        gateway: classroomId ? "HBL" : "Bank Alfalah", // Infer gateway
        timestamp: new Date().toISOString(),
      };

      setPaymentDetails(details);

      // Determine payment status
      // RC=00 means success (Bank Alfalah)
      // HBL: responseCode "00" or "000" means success
      // Any other value means failure
      if (rc === "00" || responseCode === "00" || responseCode === "000") {
        setPaymentStatus("success");
      } else if (rc || responseCode) {
        // If RC or responseCode exists but is not "00", it's a failure
        setPaymentStatus("failure");
      } else {
        // No response code found
        setPaymentStatus("error");
      }
    } catch (error) {
      console.error("Error processing payment response:", error);
      setPaymentStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToDashboard = () => {
    if (paymentDetails?.classroomId) {
      // Navigate to specific classroom if classroomId is available
      navigate(`/student/myClassrooms/${paymentDetails.classroomId}/dashboard`);
    } else {
      // Navigate to classrooms list if no specific classroom
      navigate("/student/myClassrooms");
    }
  };

  const handleTryAgain = () => {
    if (paymentDetails?.classroomId) {
      navigate(`/student/myClassrooms/${paymentDetails.classroomId}`);
    } else {
      navigate("/student/myClassrooms");
    }
  };

  const handleContactSupport = () => {
    // You can navigate to support page or open a modal
    navigate("/student/support");
  };

  if (loading) {
    return (
      <div className="payment-callback-page">
        <div className="callback-container">
          <div className="loading-state">
            <Spinner animation="border" variant="primary" />
            <h2 className="mt-3">Processing Payment...</h2>
            <p className="text-muted">
              Please wait while we verify your payment
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-callback-page">
      <div className="callback-container">
        {/* Success State */}
        {paymentStatus === "success" && (
          <div className="payment-success">
            <div className="icon-container success">
              <CheckCircle size={80} />
            </div>
            <h1 className="status-title">Payment Successful!</h1>
            <p className="status-message">
              {paymentDetails?.responseMessage ||
                "Thank you for your payment. Your transaction was completed successfully."}
            </p>

            {paymentDetails?.transactionId && (
              <div className="transaction-details">
                <div className="detail-row">
                  <span className="detail-label">Transaction ID:</span>
                  <span className="detail-value">
                    {paymentDetails.transactionId}
                  </span>
                </div>
                {paymentDetails?.amount && (
                  <div className="detail-row">
                    <span className="detail-label">Amount:</span>
                    <span className="detail-value">
                      AED {paymentDetails.amount}
                    </span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Payment Gateway:</span>
                  <span className="detail-value">{paymentDetails.gateway}</span>
                </div>
              </div>
            )}

            <div className="success-actions">
              <Button
                variant="success"
                size="lg"
                onClick={handleNavigateToDashboard}
              >
                <i className="fas fa-home me-2"></i>
                Go to Dashboard
              </Button>
            </div>

            <div className="success-footer">
              <p>Welcome to your learning journey! 🎉</p>
            </div>
          </div>
        )}

        {/* Failure State */}
        {paymentStatus === "failure" && (
          <div className="payment-failure">
            <div className="icon-container failure">
              <XCircle size={80} />
            </div>
            <h1 className="status-title">Payment Failed</h1>
            <p className="status-message">
              {paymentDetails?.responseMessage ||
                "Unfortunately, your payment could not be processed."}
            </p>

            {paymentDetails?.transactionId && (
              <div className="transaction-details">
                <div className="detail-row">
                  <span className="detail-label">Transaction ID:</span>
                  <span className="detail-value">
                    {paymentDetails.transactionId}
                  </span>
                </div>
                {paymentDetails?.responseCode && (
                  <div className="detail-row">
                    <span className="detail-label">Response Code:</span>
                    <span className="detail-value">
                      {paymentDetails.responseCode}
                    </span>
                  </div>
                )}
              </div>
            )}

            <Alert variant="info" className="mt-3">
              <AlertCircle size={16} className="me-2" />
              Please check your payment details and try again. If the problem
              persists, contact support.
            </Alert>

            <div className="failure-actions">
              <Button variant="primary" onClick={handleTryAgain}>
                <i className="fas fa-redo me-2"></i>
                Try Again
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleContactSupport}
              >
                <i className="fas fa-headset me-2"></i>
                Contact Support
              </Button>
            </div>
          </div>
        )}

        {/* Error State */}
        {paymentStatus === "error" && (
          <div className="payment-error">
            <div className="icon-container error">
              <AlertCircle size={80} />
            </div>
            <h1 className="status-title">Something Went Wrong</h1>
            <p className="status-message">
              We couldn't process your payment response. Please contact support
              with your payment details.
            </p>

            <div className="error-actions">
              <Button variant="primary" onClick={handleNavigateToDashboard}>
                <i className="fas fa-home me-2"></i>
                Go to Dashboard
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleContactSupport}
              >
                <i className="fas fa-headset me-2"></i>
                Contact Support
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
